import { useCallback, useEffect, useState } from 'react';
import { KeyRound, Loader2, ShieldAlert, RefreshCw } from 'lucide-react';
import * as api from '../../lib/api';

/**
 * The tenant's gate code, with the time it has left.
 *
 * The code is derived from a secret and the clock, so it is fetched rather
 * than stored and replaced when its window ends. A static code becomes a
 * permanent key the moment it is screenshotted or forwarded; this one stops
 * working within a couple of minutes.
 *
 * The countdown is the point. Without it a tenant has no way to know the
 * number in front of them has gone stale, and "the code didn't work" is the
 * support call this whole design exists to avoid.
 */
export default function GateCode({ leaseId }) {
  const [state, setState] = useState({ status: 'loading' });
  const [remaining, setRemaining] = useState(0);

  const load = useCallback(async () => {
    try {
      const payload = await api.leaseAccessCode(leaseId);
      setState({ status: 'ready', ...payload });
      setRemaining(payload.seconds_remaining ?? 0);
    } catch (err) {
      setState({ status: 'error', message: err.message });
    }
  }, [leaseId]);

  useEffect(() => { load(); }, [load]);

  // Tick locally rather than polling every second, then refetch once the
  // window actually closes. One request per code, not sixty.
  useEffect(() => {
    if (state.status !== 'ready' || !state.rotating) return undefined;
    if (remaining <= 0) { load(); return undefined; }
    const timer = setTimeout(() => setRemaining((r) => r - 1), 1000);
    return () => clearTimeout(timer);
  }, [state.status, state.rotating, remaining, load]);

  if (state.status === 'loading') {
    return (
      <div className="flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Getting your code…
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
        <span>
          {state.message}
          <button type="button" onClick={load} className="ml-2 font-bold underline">Try again</button>
        </span>
      </div>
    );
  }

  // Credentials issued before rotation existed still have a static code.
  if (!state.rotating) {
    return (
      <div className="rounded-lg bg-slate-100 px-3 py-2">
        <p className="text-[12px] font-bold uppercase tracking-wide text-slate-500">Keypad code</p>
        <p className="font-mono text-xl font-bold tracking-widest text-slate-900">{state.code}</p>
      </div>
    );
  }

  const nearlyOver = remaining <= 10;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <p className="flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wide text-slate-500">
        <KeyRound className="h-3.5 w-3.5 text-brand-600" aria-hidden="true" /> Gate code
      </p>
      <p className="mt-1 font-mono text-3xl font-extrabold tracking-[0.2em] text-slate-900"
         aria-live="polite">
        {state.code}
      </p>

      <div className="mt-2 flex items-center gap-2">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-200">
          <div className={`h-full rounded-full transition-[width] duration-1000 ease-linear ${
                 nearlyOver ? 'bg-amber-500' : 'bg-brand-500'}`}
               style={{ width: `${Math.max(0, (remaining / (state.step_seconds || 60)) * 100)}%` }} />
        </div>
        <span className={`w-16 shrink-0 text-right text-xs font-semibold tabular-nums ${
          nearlyOver ? 'text-amber-700' : 'text-slate-500'}`}>
          {remaining > 0 ? `${remaining}s left` : 'refreshing'}
        </span>
      </div>

      <p className="mt-2 flex items-center gap-1 text-[12px] text-slate-500">
        <RefreshCw className="h-3 w-3" aria-hidden="true" />
        Changes every {state.step_seconds}s. The previous one still works for a minute, so there
        is time to walk to the gate.
      </p>

      {/* The keypad code is a different number and a tenant standing at the
          gate needs to know which one to type. Showing only the rotating code
          while the hardware wants the static one is how someone ends up locked
          out holding a phone with the right screen open. */}
      {state.keypad_code && (
        <div className="mt-3 flex items-center justify-between gap-3 border-t border-slate-100 pt-3">
          <span className="text-[12px] text-slate-500">
            If the keypad asks for a fixed code
          </span>
          <code className="rounded-md bg-slate-100 px-2.5 py-1 font-mono text-sm font-bold tracking-widest text-slate-900">
            {state.keypad_code}
          </code>
        </div>
      )}
    </div>
  );
}
